import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { reportStatusLabel, type ReportStatus } from "@/lib/admin-labels";

export const Route = createFileRoute("/admin/reporty")({ component: ReportsPage });

type FilterTab = "all" | ReportStatus;

const statusVariant: Record<ReportStatus, "default" | "secondary" | "destructive"> = {
  open: "destructive", resolved: "default", dismissed: "secondary",
};

function ReportsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<FilterTab>("open");

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ["admin", "reports"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reports")
        .select("id,reason,detail,status,created_at,reported_by,businesses(name),profiles:reported_by(email)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ReportStatus }) => {
      const patch = status === "open"
        ? { status }
        : { status, resolved_at: new Date().toISOString() };
      const { error } = await supabase.from("reports").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "reports"] });
      qc.invalidateQueries({ queryKey: ["admin", "businesses"] });
      qc.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setStatus = (id: string, status: ReportStatus, msg: string) =>
    updateMut.mutate({ id, status }, { onSuccess: () => toast.success(msg) });

  const filtered = useMemo(
    () => (tab === "all" ? reports : reports.filter((r) => r.status === tab)),
    [reports, tab],
  );

  const counts = useMemo(() => ({
    all: reports.length,
    open: reports.filter((r) => r.status === "open").length,
    resolved: reports.filter((r) => r.status === "resolved").length,
    dismissed: reports.filter((r) => r.status === "dismissed").length,
  }), [reports]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl">Reporty od uživatelů</h1>
        <p className="text-sm text-muted-foreground">Stížnosti na podniky čekající na posouzení moderátorem.</p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as FilterTab)}>
        <TabsList>
          <TabsTrigger value="open">Otevřené ({counts.open})</TabsTrigger>
          <TabsTrigger value="resolved">Vyřešené ({counts.resolved})</TabsTrigger>
          <TabsTrigger value="dismissed">Zamítnuté ({counts.dismissed})</TabsTrigger>
          <TabsTrigger value="all">Vše ({counts.all})</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid gap-3">
        {isLoading && <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Načítám…</CardContent></Card>}
        {!isLoading && filtered.length === 0 && (
          <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Žádné reporty v této kategorii.</CardContent></Card>
        )}
        {filtered.map((r) => {
          const biz = r.businesses as { name: string } | null;
          const reporter = r.profiles as { email: string | null } | null;
          return (
            <Card key={r.id}>
              <CardContent className="flex flex-wrap items-start justify-between gap-4 p-5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{biz?.name ?? "—"}</span>
                    <Badge variant={statusVariant[r.status as ReportStatus]}>{reportStatusLabel[r.status as ReportStatus]}</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("cs-CZ")}</span>
                  </div>
                  <div className="mt-1 text-sm font-medium text-primary">{r.reason}</div>
                  {r.detail && <p className="mt-1 text-sm text-muted-foreground">{r.detail}</p>}
                  <p className="mt-2 text-xs text-muted-foreground">
                    Nahlásil/a: <span className="font-mono">{reporter?.email ?? "—"}</span>
                  </p>
                </div>
                {r.status === "open" && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setStatus(r.id, "resolved", "Report vyřešen")}>
                      <CheckCircle2 className="mr-1 h-4 w-4" /> Vyřešit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "dismissed", "Report zamítnut")}>
                      <XCircle className="mr-1 h-4 w-4" /> Zamítnout
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
